import React, { useState, useEffect } from 'react';
import { Alert, Button, Box, Typography, LinearProgress } from '@mui/material';
import { useAuth } from '../auth/AuthProvider';

interface SessionWarningProps {
  warningThreshold?: number; // Minutes before expiration to show warning
  autoExtendSession?: boolean;
}

export const SessionWarning: React.FC<SessionWarningProps> = ({
  warningThreshold = 5,
  autoExtendSession = true
}) => {
  const { user, signOut, extendSession: authExtendSession } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [timeLeft, setTimeLeft] = useState(0);
  const [progress, setProgress] = useState(100);
  const [extending, setExtending] = useState(false);

  useEffect(() => {
    if (!user?.accessToken) return;

    const checkTokenExpiry = () => {
      try {
        const token = user.accessToken;
        let expiryTime: number | null = null;

        if (token.includes('.')) {
          const payload = JSON.parse(atob(token.split('.')[1]));
          expiryTime = payload?.exp ? payload.exp * 1000 : null;
        } else if (user.tokenExpiry) {
          expiryTime = user.tokenExpiry;
        } else {
          const storedExpiry = localStorage.getItem('tokenExpiry');
          expiryTime = storedExpiry ? parseInt(storedExpiry, 10) : null;
        }

        if (!expiryTime) {
          setShowWarning(false);
          setTimeLeft(0);
          setProgress(100);
          return;
        }

        const warningTime = expiryTime - (warningThreshold * 60 * 1000);
        const now = Date.now();

        if (now > warningTime && now < expiryTime) {
          const remainingTime = Math.max(0, Math.ceil((expiryTime - now) / 1000));
          const totalWarningTime = warningThreshold * 60;
          const progressPercent = totalWarningTime > 0 ? (remainingTime / totalWarningTime) * 100 : 0;

          setShowWarning(true);
          setTimeLeft(remainingTime);
          setProgress(Math.max(0, progressPercent));
        } else if (now >= expiryTime) {
          console.warn('Session expired, signing out user');
          signOut(true); // Skip external logout for automatic expiration
        } else {
          setShowWarning(false);
          setTimeLeft(0);
          setProgress(100);
        }
      } catch (error) {
        console.error('Error checking token expiry:', error);
        setShowWarning(false);
        setTimeLeft(0);
        setProgress(100);
      }
    };

    // Check immediately
    checkTokenExpiry();

    // Check every 10 seconds
    const interval = setInterval(checkTokenExpiry, 10000);

    return () => clearInterval(interval);
  }, [user?.accessToken, user?.tokenExpiry, signOut, warningThreshold]);

  const extendSession = async () => {
    setExtending(true);
    try {
      const success = await authExtendSession();
      if (success) {
        setShowWarning(false);
        setTimeLeft(0);
        setProgress(100);
      }
    } catch (error) {
      console.error('Failed to extend session:', error);
    } finally {
      setExtending(false);
    }
  };

  const dismissWarning = () => {
    setShowWarning(false);
  };

  if (!showWarning) return null;

  const minutes = Math.floor(timeLeft / 60);
  const seconds = timeLeft % 60;

  return (
    <Box
      sx={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        boxShadow: 3
      }}
    >
      <Alert 
        severity="warning"
        action={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Button 
              color="warning" 
              size="small" 
              variant="outlined"
              onClick={extendSession}
              disabled={extending}
            >
              {extending ? 'Extending...' : 'Extend Session'}
            </Button>
            <Button 
              color="warning" 
              size="small"
              onClick={dismissWarning}
            >
              Dismiss
            </Button>
          </Box>
        }
      >
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
            Session Expiring Soon
          </Typography>
          <Typography variant="body2">
            Your session will expire in {minutes}:{seconds.toString().padStart(2, '0')}.
            {autoExtendSession && ' Click "Extend Session" to continue working.'}
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={progress} 
            sx={{ mt: 1, height: 6, borderRadius: 3 }}
            color="warning"
          />
        </Box>
      </Alert>
    </Box>
  );
};